<template>
  <div class="input" :class="[isFilled ? 'filled' : '', error ? 'error' : '']">
    <label :for="inputId">{{ label }}</label>
    <select
      v-if="type === 'select'"
      v-model="inputValue"
      :placeholder="error ? `${label} (${error})` : label"
      :required="required"
      :disabled="disabled"
    >
      <option value="" disabled selected>{{ label }}</option>
      <option
        v-for="selectOption in selectOptions"
        :key="selectOption.id"
        :value="selectOption"
      >
        {{
          selectObjectGetter ? selectObjectGetter(selectOption) : selectOption
        }}
      </option>
    </select>

    <input
      v-else-if="mask"
      :id="inputId"
      v-model="inputValue"
      v-mask="mask"
      :type="type"
      :placeholder="error ? `${label} (${error})` : label"
      :required="required"
      :disabled="disabled"
      :autocomplete="autocomplete"
      v-bind="communAttrs"
      @input="updateValue"
      @change="update"
      @keyup="update"
      @focus="$emit('focus')"
    />

    <input
      v-else-if="v4Mask"
      :id="inputId"
      v-model="inputValue"
      v-v4-mask="v4Mask"
      :type="type"
      :placeholder="error ? `${label} (${error})` : label"
      :required="required"
      :disabled="disabled"
      :autocomplete="autocomplete"
      v-bind="communAttrs"
      @input="updateValue"
      @change="update"
      @keyup="update"
      @focus="$emit('focus')"
    />

    <input
      v-else
      :id="inputId"
      v-model="inputValue"
      :type="type"
      :placeholder="error ? `${label} (${error})` : label"
      :required="required"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :maxlength="maxlength"
      v-bind="communAttrs"
      @input="updateValue"
      @change="update"
      @keyup="update"
      @focus="$emit('focus')"
    />
    <span
      :data-title="informationText"
      :class="[
        'input__information tooltip floating-label__tooltip hide',
        { 'input__information--show': informationText },
      ]"
    >
      <information />
    </span>
  </div>
</template>

<script>
import { mask } from 'vue-the-mask'
import Information from '../icons/information/Information.vue'
import BaseInput from '../product/form/BaseInput'

export default {
  components: { Information },
  directives: { mask },
  extends: BaseInput,
  inheritAttrs: false,
  props: {
    v4Mask: {
      type: [String, null],
      default: null,
    },
    autocomplete: {
      type: String,
      default: 'on',
    },
    id: {
      type: String,
      default: null,
    },
    type: {
      type: String,
      default: 'text',
    },
    label: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number, Object],
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [String, Number],
      default: null,
    },
    mask: {
      type: [String, Array],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selectOptions: {
      type: Array,
      default: null,
    },
    selectObjectGetter: {
      type: Function,
      default: null,
    },
    informationText: {
      type: String,
      default: null,
    },
    maxlength: {
      type: String,
      default: null,
    },
  },
  computed: {
    isFilled() {
      return this.isEmptyValue(this.inputValue)
    },
    inputId() {
      return this.id ? this.id : this._uid
    },
    communAttrs() {
      return {
        ...this.$attrs,
      }
    },
  },
  methods: {
    updateValue(e) {
      this.$emit('input', e.target.value)
    },
    isEmptyValue(val) {
      return val !== '' && val !== null
    },
    update(e) {
      this.$emit('update', e)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/_variables.scss';
@import '~/assets/css/tooltip.scss';
@import '~/assets/css/floating-label.scss';

$screen_desktop: 1200px;
$screen_huge: 1200px;

$color_primary: #ec8ab3;
$color_text: #383838;
$color_line: #e2e2e6;
$color_error: #f05b78;
$color_gray: #e2e2e6;
$color_gray_text: #929296;
$border_radius: 3px;

$form_input_height: 50px;

$bg_image: #f6f6f9 asset-url('hero-bg.png') repeat;

.input {
  display: flex;
  align-items: center;
  margin: 5px 0;
  position: relative;

  .input__information {
    display: none;
    position: absolute;
    top: 17px;
    right: 5px;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  .input__information--show {
    display: block;
  }

  &.hidden {
    display: none;
  }

  &.input--error {
    background-color: $color_error;
    box-shadow: 0 0 0 2px $color_error;
    color: white;
    border-radius: $border_radius;

    input,
    select {
      box-shadow: 0 0 0 1px $color_error;
      border-color: $color_error;
    }

    .hint {
      display: none;
    }

    .error {
      display: block;
      font-size: 13px;
      padding: 5px 12px;
    }
  }

  .hint {
    display: block;
    font-size: 11px;
    margin: 5px 8px 0;
    color: $color_gray_text;
  }

  input,
  select,
  label {
    transition: 0.2s ease all;
    -moz-transition: 0.2s ease all;
    -webkit-transition: 0.2s ease all;
  }

  input,
  select {
    box-sizing: border-box;
    background-color: white;
    height: $form_input_height;
    line-height: normal;
    padding: 15px;
    width: 100%;
    border-radius: $border_radius;
    border-width: 0;
    box-shadow: 0 0 0 1px $color_gray;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;

    &:focus {
      box-shadow: 0 0 0 1px #333;
    }
  }

  label {
    opacity: 0;
    position: absolute;
    left: 15px;
    top: 22px;
    font-size: 15px;
    transform: translateY(-50%);
    height: 11px;
    line-height: 12px;
    margin: 0;
    padding: 0;
    background: 0 0;
    cursor: text;
  }

  &.filled,
  &.select {
    label {
      color: #454f5b;
      opacity: 1;
      font-size: 11px;
      top: 14px;
      z-index: 1;
    }
    input,
    select {
      padding-top: 18px;
      padding-bottom: 5px;
    }
  }
}

input,
label {
  transition: 0.2s ease all;
  -moz-transition: 0.2s ease all;
  -webkit-transition: 0.2s ease all;
}

.input {
  input[type='text'],
  input[type='email'],
  input[type='number'],
  select {
    &[disabled='disabled'] {
      background-color: #f2f2f6;
    }
  }
}

input[type='text'],
input[type='email'],
input[type='number'] {
  box-sizing: border-box;
  background-color: white;
  height: $form_input_height;
  line-height: normal;
  padding: 10px 15px;
  width: 100%;
  border-radius: $border_radius;
  border-width: 0;
  box-shadow: 0 0 0 1px $color_gray;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  &:focus {
    box-shadow: 0 0 0 1px #626266;
  }
}

.error {
  color: $color_error_light;

  input {
    background-color: $color_error_background;
    border: 1px solid $color_error_light;

    &:focus {
      box-shadow: 0 0 0 1px $color_error_light;
    }
  }
}
</style>
